import { html, View } from 'rune-ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import {
  CreatorProductDefaultData,
  MODE,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { compact, isEmpty, pipe, toArray } from '@fxts/core';
import { Input_Delivery } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Delivery.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import { MShopStudioPbProductTmplS } from '@modules/MShop/Studio/PbProduct/S/Tmpl/module/MShopStudioPbProductTmplS';
import common_klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/styles/common.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import { FORM_DROPDOWN_CONSTANT } from '@services/MarppleShop/shared/components/cells/FormDropDown/type.ts';
import { GOODS_TYPE } from '@services/MarppleShop/features/Product/type';
import { CreatorProductState } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/types.ts';
import { Input_TextEditor_Intl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_TextEditor_Intl.ts';
import klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/Section.module.scss';
import { Input_RadioGroup } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_RadioGroup.ts';
import { subscribeStore } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { unEscaper } from '@shared/utils/escape.ts';
import { required_html } from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { ShippingDepartureDayView } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/ShippingDepartureDayView.ts';

interface DeliveryAndRefundInfoSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
  stores_delivery_companies: CreatorProductDefaultData['stores_delivery_companies'];
  goods_type_id: CreatorProductDefaultData['goods_type_id'];
  is_created_sku?: boolean;
}

export class DeliveryAndRefundInfoSection<T extends MODE> extends BaseSection<
  T,
  DeliveryAndRefundInfoSectionData<T>
> {
  protected readonly sectionTitle = '배송/환불 정보';
  private store = this.data.store;

  private inputMap = {
    [formKeyMap.refund_info]: this.isIntlFormData(this.store.formData, this.store.mode)
      ? new Input_TextEditor_Intl({
          kr: {
            name: formKeyMap.refund_info,
            value: isEmpty(this.store.formData.refund_info)
              ? MShopStudioPbProductTmplS.refundInfoNormalEx
              : unEscaper(this.store.formData.refund_info),
            title: html`
              <div class="${common_klass.title_with_tooltip}">
                <span class="${common_klass.text} ${shareFont('14_bold')}">교환/환불 안내</span>
                ${required_html}
                ${new Tooltip({
                  id: formKeyMap.refund_info,
                  content: html`
                    <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>·</span>
                        <span>상품 형태에 맞는 교환/안내 문구를 작성할 수 있습니다.</span>
                      </div>
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>·</span>
                        <span
                          >전자상거래법 제13조에 따라 해당 내용들은 필수로 기재 되어야 할 항목입니다.</span
                        >
                      </div>
                      <div class="${common_klass.tooltip_content_divider}">
                        <span>&nbsp;</span>
                        <a
                          class="${common_klass.atag}"
                          href="https://www.law.go.kr/%EB%B2%95%EB%A0%B9/%EC%A0%84%EC%9E%90%EC%83%81%EA%B1%B0%EB%9E%98%20%EB%93%B1%EC%97%90%EC%84%9C%EC%9D%98%20%EC%86%8C%EB%B9%84%EC%9E%90%EB%B3%B4%ED%98%B8%EC%97%90%20%EA%B4%80%ED%95%9C%20%EB%B2%95%EB%A5%A0/%EC%A0%9C13%EC%A1%B0"
                          target="_blank"
                          >전자상거래법 제13조 보러가기</a
                        >
                      </div>
                    </div>
                  `,
                  size: 'x_small',
                  variant: 'studio',
                })}
              </div>
            `,
            title_right_klass: klass.section_text_editor_title_intl,
            onFocusout: (view) => {
              this.data.store.actions.setField(formKeyMap.refund_info, view.getValue() || undefined);
            },
            option: {
              min_rows: 2,
              max_rows: 15,
              placeholder: '교환/환불 안내에 대한 내용을 입력해 주세요.',
            },
          },
          en: {
            required: true,
            name: formKeyMap.refund_info_en,
            value: isEmpty(this.store.formData.refund_info_en)
              ? MShopStudioPbProductTmplS.refundInfoNormalEx
              : unEscaper(this.store.formData.refund_info_en),
            title: '',
            title_klass: klass.section_text_editor_title_intl,
            title_right_klass: klass.section_text_editor_title_intl,
            onFocusout: (view) => {
              this.data.store.actions.setField(formKeyMap.refund_info_en, view.getValue() || undefined);
            },
            option: {
              min_rows: 2,
              max_rows: 15,
              placeholder: '영문 교환/환불 안내에 대한 내용을 입력해 주세요.',
            },
          },
          jp: {
            required: true,
            name: formKeyMap.refund_info_jp,
            value: isEmpty(this.store.formData.refund_info_jp)
              ? MShopStudioPbProductTmplS.refundInfoNormalEx
              : unEscaper(this.store.formData.refund_info_jp),
            title: '',
            title_klass: klass.section_text_editor_title_intl,
            title_right_klass: klass.section_text_editor_title_intl,
            onFocusout: (view) => {
              this.data.store.actions.setField(formKeyMap.refund_info_jp, view.getValue() || undefined);
            },
            option: {
              min_rows: 2,
              max_rows: 15,
              placeholder: '일문 교환/환불 안내에 대한 내용을 입력해 주세요.',
            },
          },
        })
      : new Input_TextEditor({
          name: formKeyMap.refund_info,
          value: isEmpty(this.store.formData.refund_info)
            ? MShopStudioPbProductTmplS.refundInfoNormalEx
            : unEscaper(this.store.formData.refund_info),
          title: html`
            <div class="${common_klass.title_with_tooltip}">
              <span class="${common_klass.text} ${shareFont('14_bold')}">교환/환불 안내</span>
              ${required_html}
              ${new Tooltip({
                id: formKeyMap.refund_info,
                content: html`
                  <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                    <div class="${common_klass.tooltip_content_divider}">
                      <span
                        >교환/환불 정보는
                        <a
                          class="${common_klass.atag}"
                          href="https://www.law.go.kr/%EB%B2%95%EB%A0%B9/%EC%A0%84%EC%9E%90%EC%83%81%EA%B1%B0%EB%9E%98%20%EB%93%B1%EC%97%90%EC%84%9C%EC%9D%98%20%EC%86%8C%EB%B9%84%EC%9E%90%EB%B3%B4%ED%98%B8%EC%97%90%20%EA%B4%80%ED%95%9C%20%EB%B2%95%EB%A5%A0/%EC%A0%9C13%EC%A1%B0"
                          target="_blank"
                          >전자상거래법 제13조</a
                        >에 따라 필수로 기재해 주세요.</span
                      >
                    </div>
                  </div>
                `,
                size: 'x_small',
                variant: 'studio',
              })}
            </div>
          `,
          option: {
            min_rows: 2,
            max_rows: 15,
            placeholder: '교환/환불 안내에 대한 내용을 입력해 주세요.',
          },
          onFocusout: (view) => {
            this.store.actions.setField(formKeyMap.refund_info, view.getValue() || undefined);
          },
        }),
  };

  // 배송 정책
  private deliveryPolicyInput = new Input_Delivery({
    stores_delivery_companies: this.data.stores_delivery_companies,
    stores_delivery_companies_products: isEmpty(
      this.store.formData._.product._.stores_delivery_companies_products,
    )
      ? this.data.stores_delivery_companies
          .filter((stores_delivery_company) => stores_delivery_company.is_default)
          .map((stores_delivery_company) => ({
            stores_delivery_company_id: Number(stores_delivery_company.id),
          }))
      : this.store.formData._.product._.stores_delivery_companies_products,
    is_marppleshop_delivery: this.data.goods_type_id === GOODS_TYPE.TPL,
    status:
      this.data.goods_type_id === GOODS_TYPE.TPL
        ? FORM_DROPDOWN_CONSTANT.READ_ONLY
        : FORM_DROPDOWN_CONSTANT.ACTIVE,
    setField: (keyName, value) => {
      this.data.store.actions.setField(keyName, value);
    },
  });

  // 배송 출발일 노출
  private deliveryStartInput = new Input_RadioGroup({
    required: true,
    name: formKeyMap.shipping_date_hidden,
    selected_id: String(this.store.formData?.shipping_date_hidden || false),
    items: [
      { id: 'false', display_name: '공개', value: false },
      { id: 'true', display_name: '비공개', value: true },
    ],
    onChange: (view) => {
      this.data.store.actions.setField(formKeyMap.shipping_date_hidden, view.getSelected()?.value);
      this.shippingDepartureDay.setIsVisible(!view.getSelected()?.value);
    },
    title: '배송 출발일 노출',
  });

  // 배송 출발일
  private shippingDepartureDay = new ShippingDepartureDayView({
    isVisible: !this.store.formData?.shipping_date_hidden,
    ship_date: this.store.formData._.store_product_ship_date?.ship_date ?? null,
    onShipDateChange: (shipDate) => {
      this.data.store.actions.setField(formKeyMap.ship_date, shipDate);
    },
  });

  public override inputViews(): Array<View> {
    return pipe(
      [
        this.deliveryPolicyInput,
        this.deliveryStartInput,
        this.shippingDepartureDay,
        this.inputMap.refund_info,
      ],
      compact,
      toArray,
    );
  }

  protected override onRender() {
    if (this.inputMap.refund_info instanceof Input_TextEditor) {
      subscribeStore(this.store, {
        [formKeyMap.refund_info]: this.inputMap.refund_info,
      });
    } else {
      subscribeStore(this.store, {
        [formKeyMap.refund_info]: this.inputMap.refund_info.editors.kr,
        [formKeyMap.refund_info_en]: this.inputMap.refund_info.editors.en,
        [formKeyMap.refund_info_jp]: this.inputMap.refund_info.editors.jp,
      });
    }
  }

  private isIntlFormData(
    formData: CreatorProductState<T>['formData'],
    mode: MODE,
  ): formData is CreatorProductState<typeof MODE.EDIT_TPL_INTL>['formData'] {
    return formData && mode === MODE.EDIT_TPL_INTL;
  }
}
